import React, { useCallback, useEffect, useState } from 'react';
import CustomSnackbar from '../../components/CustomSnackbar';
import SimpleSubscription from './SimpleSubscription';
import BusinessSubscription from './BusinessSubscription';
import { getSubscriptions, getBusinessSubChildren } from '../services/SubscriptionService';
import { Subscription as SubscriptionType } from '../types/Subscription';
import ActiveEmptyState from './ActiveEmptyState';
import { useTranslations } from '../../localization/useTranslations';
import InformationBox from '../../components/information-box/InformationBox';

export default function Subscriptions() {
  // Change the useState variable name to avoid conflict
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionType[] | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const translations = useTranslations();
  const subActivationSlug = window?.activateSubPageSlug || 'activate-sub';
  const [refreshToken, setRefreshToken] = useState<number>(0);
  const incRefreshToken = useCallback(() => setRefreshToken((t) => t + 1), []);

  // Close the snackbar
  const onCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // Auto-close snackbar after 6 seconds
  useEffect(() => {
    if (snackbarOpen) {
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 6000);
    }
  }, [snackbarOpen]);

  // Export subscription data to CSV
  const exportToCsv = async (subscriptionId: number) => {
    try {
      // Fetch business sub-children data for the specific subscriptionId
      const businessSubChildrenResponse = await getBusinessSubChildren(subscriptionId);
      const businessSubChildren = businessSubChildrenResponse.codes;

      // Create a CSV content string
      const csvContent =
        'data:text/csv;charset=utf-8,' +
        businessSubChildren
          .map((child) => `https://${window.location.hostname}/${subActivationSlug}/?code=${child.code || ''}`)
          .join('\n');

      // Create a temporary link and trigger a click to download the CSV
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'subscriptions.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting subscriptions to CSV', error);
      // Handle the error as needed
    }
  };

  // Fetch subscription data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getSubscriptions(true);
        const subscriptionsData = response.subscriptions;
        setSubscriptionData(subscriptionsData);
      } catch (error) {
        setSnackbarOpen(true);
        setSnackbarMessage('Error fetching subscriptions');
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const hasInformationBox =
    window.country && window.country?.toLowerCase() === 'sk' && window.crm_user && window.crm_user?.access.length > 0;

  return (
    <>
      <CustomSnackbar message={snackbarMessage} open={snackbarOpen} onClose={() => onCloseSnackbar()} />
      <div className="my-subscriptions">
        <div className="container">
          {hasInformationBox && <InformationBox />}
          <div className="my-subscriptions__inner-container">
            {/* Active Subscriptions Section */}
            <div id="my-subscriptions-active" className="my-subscriptions__section my-subscriptions__section--active">
              <div className="my-subscriptions__section-header">
                <h4 className="my-subscriptions__section-title my-subscriptions__section-title--active archivo">
                  {translations?.myAccountSubscription?.activeSubscriptionLabel || 'Active Subscriptions'}
                </h4>
              </div>

              {/* Display active subscriptions or empty state */}
              {!subscriptionData || subscriptionData.every((subscription) => subscription.status !== 'active') ? (
                <ActiveEmptyState />
              ) : (
                <table className="my-subscriptions__table my-subscriptions__table--active">
                  <thead className="my-subscriptions__table-head">
                    <tr className="my-subscriptions__table-header-row">
                      <th className="my-subscriptions__table-header basic basic-14">
                        {translations?.myAccountSubscription?.subscriptionType || 'Subscription Type'}
                      </th>
                      <th className="my-subscriptions__table-header basic basic-14">
                        {translations?.myAccountSubscription?.validFrom || 'Valid From'}
                      </th>
                      <th className="my-subscriptions__table-header basic basic-14">
                        {translations?.myAccountSubscription?.validTo || 'Valid To'}
                      </th>
                    </tr>
                  </thead>
                  <tbody id="active-subscriptions" className="my-subscriptions__table-body">
                    {subscriptionData &&
                      subscriptionData.map((subscription, index) => {
                        if (subscription.status === 'active') {
                          return (
                            <SimpleSubscription
                              key={index}
                              subscription={subscription}
                              onRecurrentPaymentStop={incRefreshToken}
                            />
                          );
                        }
                        return null;
                      })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Business Subscriptions Section */}
            {subscriptionData &&
              subscriptionData.map((subscription, index) => {
                if (subscription.meta && subscription.meta.business && subscription.meta.business.type === 'master') {
                  return (
                    <BusinessSubscription
                      key={index}
                      subscription={subscription}
                      exportToCsv={() => exportToCsv(subscription.id)}
                    />
                  );
                }
                return null;
              })}

            {/* Past Subscriptions Section */}
            <div id="my-subscriptions-past" className="my-subscriptions__section my-subscriptions__section--past">
              <div className="my-subscriptions__section-header">
                <h4 className="my-subscriptions__section-title my-subscriptions__section-title--past archivo">
                  {translations?.myAccountSubscription?.pastSubscriptionLabel || 'Past Subscriptions'}
                </h4>
              </div>

              {/* Display past subscriptions or empty state */}
              {subscriptionData && subscriptionData.length > 0 ? (
                (() => {
                  const pastSubscriptionCount = subscriptionData.filter(
                    (subscription) => subscription.status === 'after_end'
                  ).length;

                  if (pastSubscriptionCount > 0) {
                    return (
                      <div>
                        <table className="my-subscriptions__table my-subscriptions__table--past">
                          <thead className="my-subscriptions__table-head">
                            <tr className="my-subscriptions__table-header-row">
                              <th className="my-subscriptions__table-header basic basic-14">
                                {translations?.myAccountSubscription?.subscriptionType || 'Subscription Type'}
                              </th>
                              <th className="my-subscriptions__table-header basic basic-14">
                                {translations?.myAccountSubscription?.validFrom || 'Valid From'}
                              </th>
                              <th className="my-subscriptions__table-header basic basic-14">
                                {translations?.myAccountSubscription?.validTo || 'Valid To'}
                              </th>
                            </tr>
                          </thead>
                          <tbody id="past-subscriptions" className="my-subscriptions__table-body">
                            {subscriptionData.map((subscription, index) => {
                              if (subscription.status === 'after_end') {
                                return <SimpleSubscription key={index} subscription={subscription} />;
                              }
                              return null;
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  } else {
                    return (
                      <div
                        id="past-subscriptions-empty-state"
                        className="my-subscriptions__empty-container empty-state"
                      >
                        <p className="empty-message">
                          {translations?.myAccountSubscription?.noPastSubscriptions || 'No past subscriptions'}
                        </p>
                      </div>
                    );
                  }
                })()
              ) : (
                <div id="past-subscriptions-empty-state" className="my-subscriptions__empty-container empty-state">
                  <p className="empty-message">
                    {translations?.myAccountSubscription?.noPastSubscriptions || 'No past subscriptions'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
